import { lineGrams } from './grams.ts';
import type { ShoppingIngredient, ShoppingItem, ShoppingLine } from './shopping.ts';
import { UNITS } from './units.ts';

/**
 * Overlap hints (PLAN.md §7): what whole packs leave over after the planned cooking, and which
 * recipes would use it up. Pure: the device computes them offline from the mirror.
 */

/** Only perishables make hints: they keep a week at most. */
export const PERISHABLE_DAYS = 7;
/** A leftover counts when it is more than this share of a pack. */
export const LEFTOVER_SHARE = 0.2;

export interface Leftover {
  ingredientId: string;
  name: string;
  /** Grams left once the planned recipes are cooked. */
  grams: number;
  /** Share of a pack (½ leek = 0.5 of a 1-piece pack). */
  packShare: number;
  shelfDays: number;
  /** Left over in pieces when the catalog knows a piece weight ("½ leek"). */
  pieces: number | null;
}

/** Weight of one pack, when the catalog can tell. */
export function packGrams(facts: ShoppingIngredient): number | null {
  const pack = facts.packSize;
  if (!pack) return null;
  const { dimension, factor } = UNITS[pack.unit];
  if (dimension === 'mass') return pack.qty * factor;
  if (dimension === 'volume') return pack.qty * factor * (facts.densityGPerMl ?? 1);
  const perUnit = facts.unitGrams[pack.unit];
  return perUnit ? pack.qty * perUnit : null;
}

/**
 * Leftovers of the shopping list's items: bought (whole packs) − needed, for perishable
 * ingredients above a fifth of a pack, most perishable first.
 */
export function leftovers(
  items: readonly ShoppingItem[],
  catalog: ReadonlyMap<string, ShoppingIngredient>,
): Leftover[] {
  const out: Leftover[] = [];
  for (const item of items) {
    const facts = item.ingredientId ? catalog.get(item.ingredientId) : undefined;
    if (!facts || !item.packs || item.grams === null) continue;
    if (facts.shelfDays === null || facts.shelfDays > PERISHABLE_DAYS) continue;
    const perPack = packGrams(facts);
    if (!perPack) continue;
    const grams = item.packs.count * perPack - item.grams;
    if (grams <= perPack * LEFTOVER_SHARE) continue;
    out.push({
      ingredientId: facts.id,
      name: item.name,
      grams: Math.round(grams),
      packShare: grams / perPack,
      shelfDays: facts.shelfDays,
      pieces: facts.unitGrams.piece ? grams / facts.unitGrams.piece : null,
    });
  }
  return out.sort((a, b) => a.shelfDays - b.shelfDays || b.grams - a.grams);
}

export interface RecipeOverlap {
  recipeId: string;
  /** Leftovers the recipe (at its yield) would use, and how many grams of each. */
  usesUp: { leftover: Leftover; grams: number }[];
  /** Grams used up, weighted by how soon they spoil. */
  score: number;
  /** Catalog ingredients it shares with what is planned anyway. */
  shared: number;
}

/**
 * Ranks recipes for the recipe picker: most perishable leftover grams used up first, then most
 * ingredients shared with the plan. Recipes that use nothing up and share nothing are left out.
 */
export function rankByOverlap(
  recipes: readonly { id: string; yieldPortions: number }[],
  lines: readonly ShoppingLine[],
  catalog: ReadonlyMap<string, ShoppingIngredient>,
  left: readonly Leftover[],
  planned: ReadonlySet<string>,
): RecipeOverlap[] {
  const byIngredient = new Map(left.map((l) => [l.ingredientId, l]));
  const linesByRecipe = new Map<string, ShoppingLine[]>();
  for (const line of lines) {
    linesByRecipe.set(line.recipeId, [...(linesByRecipe.get(line.recipeId) ?? []), line]);
  }
  const ranked: RecipeOverlap[] = [];
  for (const recipe of recipes) {
    const used = new Map<string, number>();
    const shared = new Set<string>();
    for (const line of linesByRecipe.get(recipe.id) ?? []) {
      if (!line.ingredientId || line.optional) continue;
      if (planned.has(line.ingredientId)) shared.add(line.ingredientId);
      const leftover = byIngredient.get(line.ingredientId);
      const facts = catalog.get(line.ingredientId);
      if (!leftover || !facts) continue;
      const grams = line.grams ?? lineGrams(line, facts)?.grams ?? null;
      if (grams === null) continue;
      used.set(line.ingredientId, (used.get(line.ingredientId) ?? 0) + grams);
    }
    const usesUp = [...used].map(([id, grams]) => {
      const leftover = byIngredient.get(id) as Leftover;
      return { leftover, grams: Math.min(grams, leftover.grams) };
    });
    // A leftover that spoils in 2 days weighs more than one that keeps a week.
    const score = usesUp.reduce(
      (sum, u) => sum + u.grams * ((PERISHABLE_DAYS + 1 - u.leftover.shelfDays) / PERISHABLE_DAYS),
      0,
    );
    if (score > 0 || shared.size > 0) {
      ranked.push({ recipeId: recipe.id, usesUp, score, shared: shared.size });
    }
  }
  return ranked.sort((a, b) => b.score - a.score || b.shared - a.shared);
}
